/**
 * Family Member Media Panel
 *
 * Per-member modal for managing photos, videos, and stories without re-opening
 * the full edit dialog. Each add triggers a kid-facing notification via the
 * backend (handled in server/src/routes/family.ts), so children see fresh
 * memories appear in their family tree in near real-time.
 */

import { useRef, useState } from 'react';
import { Image as ImageIcon, Video, BookOpen, Plus, Trash2, X, Mic, Square, AudioLines } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LovedOneVoicePanel } from './LovedOneVoicePanel';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { apiPostFormData } from '@/lib/api';
import { getUploadUrl } from '@/integrations/api/client';
import {
  useMemberPhotos,
  useMemberVideos,
  useMemberStories,
  useAddMemberPhoto,
  useDeleteMemberPhoto,
  useAddMemberVideo,
  useDeleteMemberVideo,
  useAddMemberStory,
  useDeleteMemberStory,
} from '@/hooks/queries';
import { useVoiceClips, useCreateVoiceClip, useDeleteVoiceClip } from '@/hooks/queries/useVoiceVault';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import type { FamilyMemberRow } from '@/types/database';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: FamilyMemberRow | null;
}

async function uploadToBucket(bucket: 'family-photos' | 'family-videos', file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const res = await apiPostFormData<{ key: string; url: string }>(
    `/upload/${bucket}`,
    `familyMediaPanel.upload.${bucket}`,
    form
  );
  return res.key || res.url;
}

export function FamilyMemberMediaPanel({ open, onOpenChange, member }: Props) {
  const memberId = member?.id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle>{member?.name ? `Memories of ${member.name}` : 'Memories'}</DialogTitle>
          <DialogDescription>
            Add photos, videos and stories. Your child will be notified when something new is here.
          </DialogDescription>
        </DialogHeader>

        {memberId && (
          <Tabs defaultValue="photos" className="px-6 pb-6">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="photos">
                <ImageIcon className="h-3.5 w-3.5 mr-1.5" /> Photos
              </TabsTrigger>
              <TabsTrigger value="videos">
                <Video className="h-3.5 w-3.5 mr-1.5" /> Videos
              </TabsTrigger>
              <TabsTrigger value="voice">
                <Mic className="h-3.5 w-3.5 mr-1.5" /> Voice
              </TabsTrigger>
              <TabsTrigger value="stories">
                <BookOpen className="h-3.5 w-3.5 mr-1.5" /> Stories
              </TabsTrigger>
              <TabsTrigger value="reading">
                <AudioLines className="h-3.5 w-3.5 mr-1.5" /> Reading
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="max-h-[60vh] mt-4 pr-2">
              <TabsContent value="photos" className="mt-0">
                <PhotosTab memberId={memberId} />
              </TabsContent>
              <TabsContent value="videos" className="mt-0">
                <VideosTab memberId={memberId} />
              </TabsContent>
              <TabsContent value="voice" className="mt-0">
                <VoiceTab memberId={memberId} />
              </TabsContent>
              <TabsContent value="stories" className="mt-0">
                <StoriesTab memberId={memberId} />
              </TabsContent>
              <TabsContent value="reading" className="mt-0">
                <LovedOneVoicePanel
                  familyMemberId={memberId}
                  familyMemberName={member?.name ?? 'They'}
                />
              </TabsContent>
            </ScrollArea>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Photos ─────────────────────────────────────────────────────────────────

function PhotosTab({ memberId }: { memberId: string }) {
  const { data: photos = [], isLoading } = useMemberPhotos(memberId);
  const addPhoto = useAddMemberPhoto();
  const deletePhoto = useDeleteMemberPhoto();
  const fileRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleAdd = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      const key = await uploadToBucket('family-photos', pendingFile);
      await addPhoto.mutateAsync({ memberId, photo_url: key, caption: caption || null });
      setPendingFile(null);
      setCaption('');
      toast.success('Photo added');
    } catch {
      // mutationFactory already surfaces a toast on error
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Add new */}
      <div className="rounded-lg border border-dashed p-4 space-y-3">
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) setPendingFile(f);
            e.target.value = '';
          }}
        />

        {pendingFile ? (
          <div className="space-y-3">
            <div className="relative">
              <img
                src={URL.createObjectURL(pendingFile)}
                alt="preview"
                className="w-full h-40 object-cover rounded-md"
              />
              <button
                onClick={() => setPendingFile(null)}
                className="absolute top-2 right-2 w-6 h-6 rounded-full bg-destructive text-white flex items-center justify-center"
                aria-label="Remove selected photo"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
            <Textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Add a caption or memory…"
              rows={2}
            />
            <Button onClick={handleAdd} disabled={uploading} size="sm">
              {uploading ? 'Saving…' : 'Add Photo'}
            </Button>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Photo
          </Button>
        )}
      </div>

      {/* List */}
      {isLoading ? (
        <p className="text-body-sm text-muted-foreground text-center py-6">Loading…</p>
      ) : photos.length === 0 ? (
        <p className="text-body-sm text-muted-foreground text-center py-6">
          No photos yet. Add one above.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {photos.map((p) => {
            const url = getUploadUrl(p.photo_url);
            return (
              <div key={p.id} className="rounded-lg border overflow-hidden bg-card">
                {url && <img src={url} alt={p.caption ?? ''} className="w-full h-32 object-cover" />}
                <div className="p-3">
                  {p.caption && <p className="text-body-sm mb-2 line-clamp-3">{p.caption}</p>}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-caption text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => deletePhoto.mutate({ memberId, photoId: p.id })}
                    disabled={deletePhoto.isPending}
                  >
                    <Trash2 className="h-3 w-3 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Videos ─────────────────────────────────────────────────────────────────

function VideosTab({ memberId }: { memberId: string }) {
  const { data: videos = [], isLoading } = useMemberVideos(memberId);
  const addVideo = useAddMemberVideo();
  const deleteVideo = useDeleteMemberVideo();
  const fileRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleAdd = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      const key = await uploadToBucket('family-videos', pendingFile);
      await addVideo.mutateAsync({ memberId, video_url: key, title: title || null });
      setPendingFile(null);
      setTitle('');
      toast.success('Video added');
    } catch {
      // mutationFactory shows a toast
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-dashed p-4 space-y-3">
        <input
          ref={fileRef}
          type="file"
          accept="video/mp4,video/webm,video/quicktime"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) setPendingFile(f);
            e.target.value = '';
          }}
        />

        {pendingFile ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2">
              <Video className="h-4 w-4 text-primary shrink-0" />
              <span className="text-body-sm flex-1 truncate">{pendingFile.name}</span>
              <button
                onClick={() => setPendingFile(null)}
                className="text-muted-foreground hover:text-destructive"
                aria-label="Remove selected video"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title (optional)"
            />
            <Button onClick={handleAdd} disabled={uploading} size="sm">
              {uploading ? 'Uploading…' : 'Add Video'}
            </Button>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Video
          </Button>
        )}
      </div>

      {isLoading ? (
        <p className="text-body-sm text-muted-foreground text-center py-6">Loading…</p>
      ) : videos.length === 0 ? (
        <p className="text-body-sm text-muted-foreground text-center py-6">
          No videos yet. Upload one above.
        </p>
      ) : (
        <div className="space-y-3">
          {videos.map((v) => {
            const url = getUploadUrl(v.video_url);
            return (
              <div key={v.id} className="rounded-lg border bg-card p-3">
                {url && (
                  <video src={url} controls className="w-full max-h-56 rounded-md mb-2" />
                )}
                <div className="flex items-center justify-between gap-3">
                  <p className="text-body-sm truncate">{v.title || 'Untitled video'}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-caption text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => deleteVideo.mutate({ memberId, videoId: v.id })}
                    disabled={deleteVideo.isPending}
                  >
                    <Trash2 className="h-3 w-3 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Voice ───────────────────────────────────────────────────────────────────

// Read an audio file's duration (seconds) from its metadata. Falls back to 1s
// when the browser can't determine it (some WebM streams report Infinity) — the
// backend requires a positive integer.
function readAudioDuration(file: Blob): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    const done = (seconds: number) => {
      URL.revokeObjectURL(url);
      const rounded = Math.round(seconds);
      resolve(Number.isFinite(rounded) && rounded > 0 ? rounded : 1);
    };
    audio.addEventListener('loadedmetadata', () => done(audio.duration));
    audio.addEventListener('error', () => done(1));
    audio.src = url;
  });
}

// Derive a sensible file extension from a recorded blob's MIME type so the
// upload's multipart filename matches its content.
function extFromMime(mime: string): string {
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('mp4')) return 'mp4';
  if (mime.includes('mpeg') || mime.includes('mp3')) return 'mp3';
  if (mime.includes('wav')) return 'wav';
  return 'webm';
}

function mmss(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function VoiceTab({ memberId }: { memberId: string }) {
  const { data, isLoading } = useVoiceClips({ familyMemberId: memberId });
  const clips = data?.items ?? [];
  const createClip = useCreateVoiceClip();
  const deleteClip = useDeleteVoiceClip();
  const fileRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);

  const {
    state: recState,
    isRecording,
    duration: recDuration,
    audioBlob,
    audioUrl,
    startRecording,
    stopRecording,
    reset: resetRecording,
    error: recError,
  } = useAudioRecorder({ maxDuration: 180000 }); // 3 min cap

  const hasRecording = recState === 'stopped' && !!audioBlob;

  const clearPending = () => {
    setPendingFile(null);
    resetRecording();
    setTitle('');
  };

  const handleAdd = async () => {
    // Prefer a fresh recording; otherwise use an uploaded file.
    let blob: Blob | null = null;
    let filename = '';
    let seconds = 1;
    let fallbackTitle = 'Voice memory';

    if (hasRecording && audioBlob) {
      blob = audioBlob;
      filename = `voice-memory.${extFromMime(audioBlob.type)}`;
      seconds = recDuration || (await readAudioDuration(audioBlob));
    } else if (pendingFile) {
      blob = pendingFile;
      filename = pendingFile.name;
      seconds = await readAudioDuration(pendingFile);
      fallbackTitle = pendingFile.name.replace(/\.[^.]+$/, '') || fallbackTitle;
    }
    if (!blob) return;

    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', blob, filename);
      const res = await apiPostFormData<{ key: string; url: string; size: number }>(
        '/upload/voice-clips',
        'familyMediaPanel.upload.voice-clips',
        form
      );
      await createClip.mutateAsync({
        family_member_id: memberId,
        title: title.trim() || fallbackTitle,
        category: 'memory',
        audio_url: res.key || res.url,
        duration_seconds: Math.max(1, seconds),
        file_size_bytes: res.size,
      });
      clearPending();
      toast.success('Voice memory added');
    } catch {
      // useCreateVoiceClip / apiPostFormData already surface a toast on error
    } finally {
      setUploading(false);
    }
  };

  const ready = hasRecording || !!pendingFile;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-dashed p-4 space-y-3">
        <input
          ref={fileRef}
          type="file"
          accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/webm,audio/mp4,.mp3,.wav,.ogg,.webm,.m4a"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) {
              resetRecording();
              setPendingFile(f);
            }
            e.target.value = '';
          }}
        />

        {isRecording ? (
          // Actively recording
          <div className="flex items-center gap-3">
            <span className="relative flex h-3 w-3 shrink-0">
              <span className="absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75 animate-ping" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-destructive" />
            </span>
            <span className="text-body-sm font-mono flex-1">Recording… {mmss(recDuration)}</span>
            <Button onClick={stopRecording} size="sm" variant="destructive">
              <Square className="h-3.5 w-3.5 mr-1.5" /> Stop
            </Button>
          </div>
        ) : ready ? (
          // Have a recording or a selected file — review + title + save
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2">
              <Mic className="h-4 w-4 text-primary shrink-0" />
              <span className="text-body-sm flex-1 truncate">
                {hasRecording ? `Recording · ${mmss(recDuration)}` : pendingFile?.name}
              </span>
              <button
                onClick={clearPending}
                className="text-muted-foreground hover:text-destructive"
                aria-label="Discard"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            {hasRecording && audioUrl && <audio src={audioUrl} controls className="w-full" />}
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title (optional)"
            />
            <Button onClick={handleAdd} disabled={uploading} size="sm">
              {uploading ? 'Uploading…' : 'Add Voice'}
            </Button>
          </div>
        ) : (
          // Idle — record or upload
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={startRecording}>
                <Mic className="h-3.5 w-3.5 mr-1.5" /> Record
              </Button>
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Upload
              </Button>
            </div>
            {recError ? (
              <p className="text-caption text-destructive">{recError}</p>
            ) : (
              <p className="text-caption text-muted-foreground">
                Record from your mic, or upload MP3, WAV, OGG, WebM, M4A.
              </p>
            )}
          </div>
        )}
      </div>

      {isLoading ? (
        <p className="text-body-sm text-muted-foreground text-center py-6">Loading…</p>
      ) : clips.length === 0 ? (
        <p className="text-body-sm text-muted-foreground text-center py-6">
          No voice memories yet. Upload one above.
        </p>
      ) : (
        <div className="space-y-3">
          {clips.map((c) => {
            const url = getUploadUrl(c.audio_url);
            return (
              <div key={c.id} className="rounded-lg border bg-card p-3">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <p className="text-body-sm truncate">{c.title}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-caption text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
                    onClick={() => deleteClip.mutate(c.id)}
                    disabled={deleteClip.isPending}
                  >
                    <Trash2 className="h-3 w-3 mr-1" /> Delete
                  </Button>
                </div>
                {url && <audio src={url} controls className="w-full" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Stories ────────────────────────────────────────────────────────────────

function StoriesTab({ memberId }: { memberId: string }) {
  const { data: stories = [], isLoading } = useMemberStories(memberId);
  const addStory = useAddMemberStory();
  const deleteStory = useDeleteMemberStory();
  const [draft, setDraft] = useState('');

  const handleAdd = async () => {
    const content = draft.trim();
    if (!content) return;
    try {
      await addStory.mutateAsync({ memberId, content });
      setDraft('');
      toast.success('Story added');
    } catch {
      // mutationFactory shows a toast
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-dashed p-4 space-y-3">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Write a story — a memory, a quirk, something they always say…"
          rows={4}
        />
        <Button onClick={handleAdd} disabled={!draft.trim() || addStory.isPending} size="sm">
          {addStory.isPending ? 'Saving…' : 'Add Story'}
        </Button>
      </div>

      {isLoading ? (
        <p className="text-body-sm text-muted-foreground text-center py-6">Loading…</p>
      ) : stories.length === 0 ? (
        <p className="text-body-sm text-muted-foreground text-center py-6">
          No stories yet. Share the first memory above.
        </p>
      ) : (
        <div className="space-y-3">
          {stories.map((s) => (
            <div key={s.id} className="rounded-lg border bg-card p-4">
              <p className="text-body-sm whitespace-pre-wrap mb-2">{s.content}</p>
              <div className="flex items-center justify-between gap-2 text-caption text-muted-foreground">
                <span>{new Date(s.created_at).toLocaleDateString()}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-caption text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => deleteStory.mutate({ memberId, storyId: s.id })}
                  disabled={deleteStory.isPending}
                >
                  <Trash2 className="h-3 w-3 mr-1" /> Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
